import React, { useEffect, useState } from 'react';
import { getSettings, updateSettings } from '../api/adminApi';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { Save, CheckCircle, AlertCircle } from 'lucide-react';

const styles = {
  header: { marginBottom: 'var(--space-xl)' },
  title: {
    fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 700,
    color: 'var(--color-text-primary)', margin: 0, letterSpacing: '0.03em',
  },
  subtitle: { fontSize: '0.9rem', color: 'var(--color-text-muted)', margin: '4px 0 0' },
  centered: { display: 'flex', justifyContent: 'center', padding: 'var(--space-2xl)' },
  message: {
    display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
    padding: 'var(--space-sm) var(--space-md)', borderRadius: 'var(--radius-md)',
    fontSize: '0.85rem', marginBottom: 'var(--space-md)',
  },
  success: { backgroundColor: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.2)', color: 'var(--color-success)' },
  error: { backgroundColor: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.2)', color: 'var(--color-danger)' },
  groupSection: { marginBottom: 'var(--space-xl)' },
  form: { display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' },
  fieldLabel: {
    fontFamily: 'var(--font-display)', fontSize: '0.85rem', fontWeight: 500,
    color: 'var(--color-text-secondary)', letterSpacing: '0.05em', textTransform: 'uppercase',
    marginBottom: 'var(--space-xs)', display: 'block',
  },
  fieldDescription: { fontSize: '0.78rem', color: 'var(--color-text-muted)', margin: '2px 0 var(--space-xs)' },
  colorField: { display: 'flex', alignItems: 'center', gap: 'var(--space-md)' },
  colorPreview: {
    width: '48px', height: '48px', borderRadius: 'var(--radius-md)',
    border: '2px solid rgba(212,175,55,0.2)', cursor: 'pointer', flexShrink: 0,
  },
  colorInput: {
    width: '48px', height: '48px', padding: 0, border: 'none',
    borderRadius: 'var(--radius-md)', cursor: 'pointer', backgroundColor: 'transparent',
  },
  saveBar: {
    display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-lg)',
    paddingTop: 'var(--space-lg)', borderTop: '1px solid rgba(212,175,55,0.1)',
  },
};

// Only show these settings to operators — simple, non-technical
const visibleSettings = {
  general: {
    label: 'Generale',
    keys: ['game_name'],
  },
  appearance: {
    label: 'Aspetto',
    keys: ['primary_color', 'secondary_color'],
  },
};

const friendlyLabels = {
  game_name: 'Nome del gioco',
  primary_color: 'Colore principale',
  secondary_color: 'Colore secondario',
};

const fieldHelperText = {
  game_name: 'Il nome che viene mostrato durante la partita',
  primary_color: 'Il colore principale usato nel gioco (es. il colore dei pulsanti)',
  secondary_color: 'Il colore secondario usato per i dettagli',
};

export default function AdminSettings() {
  const [settingsData, setSettingsData] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await getSettings();
        setSettingsData(result);

        const values = {};
        Object.values(result).forEach((groupItems) => {
          groupItems.forEach((item) => {
            if (item.type === 'json' || item.type === 'array') {
              values[item.key] = JSON.stringify(item.value, null, 2);
            } else {
              values[item.key] = item.value != null ? String(item.value) : '';
            }
          });
        });
        setFormValues(values);
      } catch {
        setMessage({ type: 'error', text: 'Errore nel caricamento delle impostazioni' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const settingsPayload = [];

      // Send ALL settings (including hidden ones, unchanged)
      Object.values(settingsData).forEach((groupItems) => {
        groupItems.forEach((item) => {
          const rawValue = formValues[item.key];
          let parsedValue;

          if (item.type === 'json' || item.type === 'array') {
            try {
              parsedValue = JSON.parse(rawValue);
            } catch {
              parsedValue = rawValue;
            }
          } else {
            parsedValue = rawValue;
          }

          settingsPayload.push({ key: item.key, value: parsedValue });
        });
      });

      await updateSettings(settingsPayload);
      setMessage({ type: 'success', text: 'Impostazioni salvate!' });
      setTimeout(() => setMessage(null), 4000);
    } catch (err) {
      const msg = err.message || err.response?.data?.message || 'Errore nel salvataggio';
      setMessage({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={styles.centered}><LoadingSpinner size="lg" /></div>;
  }

  const renderField = (item) => {
    const value = formValues[item.key] ?? '';
    const label = friendlyLabels[item.key] || item.key;
    const helper = fieldHelperText[item.key] || item.description;

    if (item.key.includes('color')) {
      return (
        <div key={item.key}>
          <label style={styles.fieldLabel}>{label}</label>
          {helper && <p style={styles.fieldDescription}>{helper}</p>}
          <div style={styles.colorField}>
            <input
              type="color"
              value={value || '#000000'}
              onChange={(e) => handleChange(item.key, e.target.value)}
              style={styles.colorInput}
            />
            <Input
              value={value}
              onChange={(e) => handleChange(item.key, e.target.value)}
              placeholder="#D4AF37"
              style={{ flex: 1 }}
            />
            <div style={{ ...styles.colorPreview, backgroundColor: value || 'transparent' }} />
          </div>
        </div>
      );
    }

    return (
      <div key={item.key}>
        {helper && <p style={styles.fieldDescription}>{helper}</p>}
        <Input
          label={label}
          value={value}
          onChange={(e) => handleChange(item.key, e.target.value)}
        />
      </div>
    );
  };

  // Build visible groups from settingsData
  const groupOrder = ['general', 'appearance'];

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Impostazioni</h1>
        <p style={styles.subtitle}>Personalizza il tuo gioco</p>
      </div>

      {message && (
        <div style={{ ...styles.message, ...(message.type === 'success' ? styles.success : styles.error) }}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ maxWidth: '600px' }}>
          {groupOrder.map((groupKey) => {
            const groupConfig = visibleSettings[groupKey];
            if (!groupConfig) return null;
            const groupItems = settingsData[groupKey];
            if (!groupItems) return null;

            // Filter only visible keys
            const filteredItems = groupItems.filter(item => groupConfig.keys.includes(item.key));
            if (filteredItems.length === 0) return null;

            return (
              <div key={groupKey} style={styles.groupSection}>
                <Card header={groupConfig.label}>
                  <div style={styles.form}>
                    {filteredItems.map((item) => renderField(item))}
                  </div>
                </Card>
              </div>
            );
          })}
        </div>

        <div style={styles.saveBar}>
          <Button variant="primary" type="submit" loading={saving}>
            <Save size={16} />
            Salva modifiche
          </Button>
        </div>
      </form>
    </div>
  );
}
